import {
  Users,
  BarChart3,
  Smartphone,
  MapPin,
  Clock,
  Euro,
  Zap,
  FileText,
  CreditCard,
  Wifi,
  ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "../../../i18n";
import digitize from "../assets/digitize.png"; // Adjust the path as necessary
import room from "../assets/room.png"; // Adjust the path as necessary

export default function CityBenefitsSection() {
  const { t } = useTranslation();
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-purple-100 to-blue-100 text-purple-800 mb-6">
            <Users className="w-4 h-4 mr-2" />
            <span className="text-sm font-medium">{t('bikeParking.cityBenefits.optimized')}</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t('bikeParking.cityBenefits.title')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('bikeParking.cityBenefits.description')}
          </p>
        </div>

        {/* Hauptvorteile Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          <div className="text-center p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-1">
            <div className="h-16 w-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-blue-400 to-blue-600 flex items-center justify-center">
              <Users className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t('bikeParking.cityBenefits.main.unifiedTitle')}
            </h3>
            <p className="text-gray-600 text-sm">
              {t('bikeParking.cityBenefits.main.unifiedDesc')}
            </p>
          </div>

          <div className="text-center p-6 bg-gradient-to-br from-green-50 to-green-100 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-1">
            <div className="h-16 w-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-green-400 to-green-600 flex items-center justify-center">
              <BarChart3 className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t('bikeParking.cityBenefits.main.dataTitle')}
            </h3>
            <p className="text-gray-600 text-sm">
              {t('bikeParking.cityBenefits.main.dataDesc')}
            </p>
          </div>

          <div className="text-center p-6 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-1">
            <div className="h-16 w-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-purple-400 to-purple-600 flex items-center justify-center">
              <Euro className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t('bikeParking.cityBenefits.main.costTitle')}
            </h3>
            <p className="text-gray-600 text-sm">
              {t('bikeParking.cityBenefits.main.costDesc')}
            </p>
          </div>

          <div className="text-center p-6 bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-1">
            <div className="h-16 w-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-orange-400 to-orange-600 flex items-center justify-center">
              <Clock className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t('bikeParking.cityBenefits.main.fastTitle')}
            </h3>
            <p className="text-gray-600 text-sm">
              {t('bikeParking.cityBenefits.main.fastDesc')}
            </p>
          </div>
        </div>

        {/* Detaillierte Funktionen */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 mb-6">
              <MapPin className="w-4 h-4 mr-2" />
              <span className="text-sm font-medium">{t('bikeParking.cityBenefits.centralLabel')}</span>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              {t('bikeParking.cityBenefits.centralTitle')}
            </h3>
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-1">
                  <MapPin className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">
                    {t('bikeParking.cityBenefits.central.dashboardTitle')}
                  </h4>
                  <p className="text-gray-600 text-sm">
                    {t('bikeParking.cityBenefits.central.dashboardDesc')}
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-green-100 flex items-center justify-center mr-3 mt-1">
                  <Smartphone className="h-4 w-4 text-green-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">
                    {t('bikeParking.cityBenefits.central.appTitle')}
                  </h4>
                  <p className="text-gray-600 text-sm">
                    {t('bikeParking.cityBenefits.central.appDesc')}
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-purple-100 flex items-center justify-center mr-3 mt-1">
                  <BarChart3 className="h-4 w-4 text-purple-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">
                    {t('bikeParking.cityBenefits.central.analyticsTitle')}
                  </h4>
                  <p className="text-gray-600 text-sm">
                    {t('bikeParking.cityBenefits.central.analyticsDesc')}
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-orange-100 flex items-center justify-center mr-3 mt-1">
                  <Users className="h-4 w-4 text-orange-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">
                    {t('bikeParking.cityBenefits.central.userTitle')}
                  </h4>
                  <p className="text-gray-600 text-sm">
                    {t('bikeParking.cityBenefits.central.userDesc')}
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-red-100 flex items-center justify-center mr-3 mt-1">
                  <FileText className="h-4 w-4 text-red-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">
                    {t('bikeParking.cityBenefits.central.billingTitle')}
                  </h4>
                  <p className="text-gray-600 text-sm">
                    {t('bikeParking.cityBenefits.central.billingDesc')}
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-indigo-100 flex items-center justify-center mr-3 mt-1">
                  <CreditCard className="h-4 w-4 text-indigo-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">
                    {t('bikeParking.cityBenefits.central.payoutTitle')}
                  </h4>
                  <p className="text-gray-600 text-sm">
                    {t('bikeParking.cityBenefits.central.payoutDesc')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-8 shadow-lg">
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-gradient-to-br from-blue-300 to-purple-300 rounded-full opacity-30 blur-2xl"></div>
              <img
                src={room}
                alt={t('bikeParking.cityBenefits.dashboardAlt')}
                width={500}
                height={400}
                className="rounded-lg relative z-10"
              />
              <div className="absolute -bottom-4 -right-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-lg shadow-lg text-sm font-medium">
                {t('bikeParking.cityBenefits.realtimeOverlay')}
              </div>
            </div>
          </div>
        </div>

        {/* Nachrüstung bestehender Anlagen */}
        <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-8 md:p-12 shadow-lg">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1 relative">
              <div className="absolute -top-6 -left-6 w-32 h-32 bg-gradient-to-br from-green-300 to-blue-300 rounded-full opacity-30 blur-2xl"></div>
              <img
                src={digitize}
                alt={t('bikeParking.cityBenefits.retrofitAlt')}
                width={400}
                height={300}
                className="rounded-lg relative z-10"
              />
              <div className="absolute -bottom-4 -left-4 bg-gradient-to-r from-green-600 to-blue-600 text-white px-4 py-2 rounded-lg shadow-lg text-sm font-medium">
                {t('bikeParking.cityBenefits.retrofitOverlay')}
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-green-100 to-blue-100 text-green-800 mb-6">
                <Zap className="w-4 h-4 mr-2" />
                <span className="text-sm font-medium">{t('bikeParking.cityBenefits.retrofitLabel')}</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                {t('bikeParking.cityBenefits.retrofitTitle')}
              </h3>
              <p className="text-gray-600 mb-6">
                {t('bikeParking.cityBenefits.retrofitDesc')}
              </p>

              <div className="space-y-3">
                <div className="flex items-center">
                  <div className="h-6 w-6 rounded-full bg-gradient-to-r from-green-400 to-blue-400 flex items-center justify-center mr-3">
                    <Zap className="h-4 w-4 text-white" />
                  </div>
                  <span className="text-gray-700">
                    {t('bikeParking.cityBenefits.retrofit.bluetooth')}
                  </span>
                </div>
                <div className="flex items-center">
                  <div className="h-6 w-6 rounded-full bg-gradient-to-r from-green-400 to-blue-400 flex items-center justify-center mr-3">
                    <Wifi className="h-4 w-4 text-white" />
                  </div>
                  <span className="text-gray-700">
                    {t('bikeParking.cityBenefits.retrofit.iot')}
                  </span>
                </div>
                <div className="flex items-center">
                  <div className="h-6 w-6 rounded-full bg-gradient-to-r from-green-400 to-blue-400 flex items-center justify-center mr-3">
                    <Clock className="h-4 w-4 text-white" />
                  </div>
                  <span className="text-gray-700">
                    {t('bikeParking.cityBenefits.retrofit.ready')}
                  </span>
                </div>
              </div>

              <div className="mt-8">
                <Link
                  to="/marketplace/electronics"
                  className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 shadow-md transition-colors"
                >
                  {t('bikeParking.cityBenefits.marketplaceLink')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
